import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import ModernInvoicePreview from '../components/ModernInvoicePreview';

/**
 * Modern Invoice Details Page
 * 
 * Displays a full-page preview of the modern invoice with print capabilities
 */
const ModernInvoiceDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [language, setLanguage] = useState<'en' | 'de'>('en');

  useEffect(() => {
    // Get language from URL params
    const langParam = searchParams.get('language');
    if (langParam === 'de' || langParam === 'en') {
      setLanguage(langParam);
    }
  }, [searchParams]);

  const handleLanguageChange = (newLanguage: 'en' | 'de') => {
    setLanguage(newLanguage);
    // Update URL without causing a page reload
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set('language', newLanguage);
    navigate(`?${newSearchParams.toString()}`, { replace: true });
  };

  const handleBack = () => {
    navigate('/invoices');
  };

  if (!id) {
    return (
      <div className="error-page">
        <h2>❌ Invalid Invoice ID</h2>
        <p>No invoice ID provided in the URL.</p>
        <button className="btn btn-primary" onClick={handleBack}>
          ← Back to Invoices
        </button>
      </div>
    );
  }

  return (
    <div className="modern-invoice-details-page">
      {/* Page Header */}
      <div className="page-header">
        <div className="header-left">
          <button className="btn btn-secondary" onClick={handleBack}>
            ← Back to Invoices
          </button>
          <h1>Modern Invoice Preview</h1>
        </div>
        <div className="header-right">
          <div className="language-selector">
            <label>Language:</label>
            <select 
              value={language} 
              onChange={(e) => handleLanguageChange(e.target.value as 'en' | 'de')}
              className="form-control"
            >
              <option value="en">🇺🇸 English</option>
              <option value="de">🇩🇪 Deutsch</option>
            </select>
          </div>
        </div>
      </div>

      {/* Modern Invoice Preview */}
      <div className="preview-container">
        <ModernInvoicePreview
          invoiceId={id}
          language={language}
          showActions={true}
          className="full-preview"
        />
      </div>

      {/* Additional Actions */}
      <div className="additional-actions">
        <div className="action-group">
          <h3>Quick Actions</h3>
          <div className="action-buttons">
            <button 
              className="btn btn-outline-primary"
              onClick={() => navigate(`/invoices/${id}`)}
            >
              📋 View Invoice Details
            </button>
            <button 
              className="btn btn-outline-secondary"
              onClick={() => navigate(`/invoices/${id}?mode=edit`)}
            >
              ✏️ Edit Invoice
            </button>
            <button 
              className="btn btn-outline-info"
              onClick={() => window.print()}
            >
              🖨️ Print Page
            </button>
          </div>
        </div>

        <div className="info-group">
          <h3>About Modern Invoices</h3>
          <ul>
            <li>✅ Professional design with company branding</li>
            <li>✅ Multi-language support (English & German)</li>
            <li>✅ Company logo on every page</li>
            <li>✅ Invoice number in header on every page</li>
            <li>✅ Company info, legal info, and bank details in footer</li>
            <li>✅ Optimized for PDF generation and printing</li>
          </ul>
        </div>
      </div>

      {/* Styles */}
      <style dangerouslySetInnerHTML={{
        __html: `
        .modern-invoice-details-page {
          min-height: 100vh;
          background: #f8f9fa;
          padding: 20px;
        }

        .page-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 30px;
          padding: 20px;
          background: white;
          border-radius: 8px;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
        }

        .header-left {
          display: flex;
          align-items: center;
          gap: 15px;
        }

        .header-left h1 {
          margin: 0;
          font-family: 'Montserrat', sans-serif;
          font-weight: 800;
          color: #4176ba;
        }

        .header-right {
          display: flex;
          align-items: center;
          gap: 15px;
        }

        .language-selector {
          display: flex;
          align-items: center;
          gap: 10px;
        }

        .language-selector label {
          font-weight: 500;
          color: #363640;
        }

        .language-selector select {
          min-width: 150px;
        }

        .preview-container {
          margin-bottom: 40px;
        }

        .full-preview {
          max-width: none;
          margin: 0;
        }

        .additional-actions {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 30px;
          margin-top: 40px;
        }

        .action-group, .info-group {
          background: white;
          padding: 25px;
          border-radius: 8px;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
        }

        .action-group h3, .info-group h3 {
          margin-top: 0;
          margin-bottom: 20px;
          font-family: 'Montserrat', sans-serif;
          font-weight: 700;
          color: #4176ba;
        }

        .action-buttons {
          display: flex;
          flex-direction: column;
          gap: 10px;
        }

        .action-buttons .btn {
          justify-content: flex-start;
          text-align: left;
        }

        .info-group ul {
          margin: 0;
          padding-left: 0;
          list-style: none;
        }

        .info-group li {
          padding: 8px 0;
          border-bottom: 1px solid #e2e3e7;
          font-size: 14px;
        }

        .info-group li:last-child {
          border-bottom: none;
        }

        .error-page {
          text-align: center;
          padding: 60px 20px;
          background: white;
          border-radius: 8px;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
          margin: 40px auto;
          max-width: 500px;
        }

        .error-page h2 {
          color: #dc3545;
          margin-bottom: 15px;
        }

        .error-page p {
          color: #6c757d;
          margin-bottom: 25px;
        }

        @media (max-width: 768px) {
          .page-header {
            flex-direction: column;
            gap: 15px;
            text-align: center;
          }

          .header-left {
            flex-direction: column;
            gap: 10px;
          }

          .additional-actions {
            grid-template-columns: 1fr;
            gap: 20px;
          }

          .action-buttons {
            flex-direction: row;
            flex-wrap: wrap;
          }

          .modern-invoice-details-page {
            padding: 10px;
          }
        }

        @media print {
          .page-header,
          .additional-actions {
            display: none !important;
          }

          .modern-invoice-details-page {
            background: white;
            padding: 0;
          }

          .preview-container {
            margin: 0;
          }
        }
        `
      }} />
    </div>
  );
};

export default ModernInvoiceDetails;